import { Directive, ElementRef, Input, OnChanges, OnDestroy, SimpleChanges, afterNextRender, inject, numberAttribute } from '@angular/core';
import { SplitText, gsap } from '../gsap';
import { QUERIES } from '../motion-env';
import { MotionService } from '../motion.service';

export type SplitMode = 'intro' | 'reveal' | 'scrub';

/**
 * Splits a HEADING into words or lines and animates them. The directive owns the element's
 * text: bind it with `[appSplitText]="'key' | translate"` (never `{{ }}` inside the element),
 * so a language change reverts the old split, writes the new text and re-splits.
 * Never splits into characters, so Armenian punctuation stays attached to its word.
 */
@Directive({ selector: '[appSplitText]' })
export class SplitTextDirective implements OnChanges, OnDestroy {
  @Input('appSplitText') text: string | null | undefined = '';
  @Input() splitMode: SplitMode = 'reveal';
  /** s; intro mode, first play only. */
  @Input({ transform: numberAttribute }) splitDelay = 0;
  /** Default 'top 80%' (reveal) / 'top 75%' (scrub). */
  @Input() splitStart = '';
  /** Scrub mode only. */
  @Input() splitEnd = 'center 45%';

  private readonly el: HTMLElement = inject(ElementRef).nativeElement;
  private readonly motion = inject(MotionService);
  private ready = false;
  private played = false;
  private token = 0;
  private mm?: ReturnType<typeof gsap.matchMedia>;
  private destroyed = false;

  constructor() {
    this.el.classList.add('dv-split');
    afterNextRender(() => {
      this.el.classList.add(`dv-split--${this.splitMode}`);
      const start = () => {
        if (this.destroyed) return;
        this.ready = true;
        this.rebuild();
      };
      // Intro headlines are above the fold by design; other modes wait for the first paint
      // unless they are already on screen.
      if (this.splitMode === 'intro') this.motion.outside(start);
      else this.motion.whenNeeded(this.el, start);
    });
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['text']) return;
    this.motion.outside(() => (this.ready ? this.rebuild() : this.writeText()));
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.token++;
    this.mm?.revert();
  }

  private writeText(): void {
    this.el.textContent = this.text ?? '';
  }

  private rebuild(): void {
    const token = ++this.token;
    // Revert BEFORE writing: revert restores the HTML captured at split time.
    this.mm?.revert();
    this.mm = undefined;
    this.writeText();
    if (!this.text) return;

    if (this.splitMode === 'reveal' && (this.played || this.motion.recentLanguageSwitch())) {
      this.played = true;
      this.motion.requestRefresh();
      return;
    }

    const mm = (this.mm = gsap.matchMedia(this.el));
    // Only `motion`: a breakpoint condition would revert, re-split and replay the heading on
    // every resize or rotation across it.
    mm.add({ motion: QUERIES.motion }, (ctx) => {
      if (!ctx.conditions!['motion']) return;
      if (this.splitMode !== 'scrub') gsap.set(this.el, { opacity: 0 });
      this.motion.fontsReady.then(() => {
        if (token !== this.token || ctx.isReverted) return;
        ctx.add(() => this.createSplit());
        this.motion.requestRefresh();
      });
    });
  }

  private createSplit(): void {
    const el = this.el;
    const aria = /^H[1-6]$/.test(el.tagName) ? 'auto' : 'none';

    if (this.splitMode === 'intro') {
      const split = SplitText.create(el, { type: 'words', mask: 'words', wordsClass: 'split-word', aria });
      gsap.set(el, { opacity: 1 });
      const replay = this.played || this.motion.recentLanguageSwitch();
      gsap.from(split.words, {
        yPercent: 110,
        duration: replay ? 0.6 : 0.9,
        ease: replay ? 'power3.out' : 'power4.out',
        stagger: replay ? 0.04 : 0.06,
        delay: replay ? 0 : this.splitDelay,
      });
      this.played = true;
      return;
    }

    if (this.splitMode === 'reveal') {
      SplitText.create(el, {
        type: 'lines',
        mask: 'lines',
        linesClass: 'split-line',
        autoSplit: true,
        aria,
        onSplit: (split) => {
          gsap.set(el, { opacity: 1 });
          // A re-split after the reveal played (late web font, width change) must not re-arm
          // the hidden from-state behind a fresh once-trigger.
          if (this.played) return;
          // Returning the tween lets autoSplit re-sync it after a re-split.
          return gsap.from(split.lines, {
            yPercent: 105,
            duration: 0.9,
            ease: 'expo.out',
            stagger: 0.1,
            scrollTrigger: {
              trigger: el,
              start: this.splitStart || 'top 80%',
              once: true,
              onEnter: () => {
                this.played = true;
              },
            },
          });
        },
      });
      return;
    }

    const split = SplitText.create(el, { type: 'words', wordsClass: 'split-word', aria });
    gsap.fromTo(
      split.words,
      { opacity: 0.2 },
      {
        opacity: 1,
        stagger: 0.12,
        ease: 'none',
        scrollTrigger: { trigger: el, start: this.splitStart || 'top 75%', end: this.splitEnd, scrub: true },
      }
    );
  }
}
